import React, { useState } from 'react'
import {
  Box,
  Button, IconButton, Theme, Typography, useTheme, TextField,
} from '@mui/material'
import { EntityData } from '@3m5/crude-frontend/dist/shared/types/dataTypes'
import Env from '../../shared/config/envConfig'
import { addCsrfHeaders } from '../../shared/config/csrf'
import DialogComponent from '@3m5/crude-frontend/dist/shared/components/MessageComponents/DialogComponent'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import VisibilityIcon from '@mui/icons-material/Visibility'

interface InjectedTableActionComponentProps {
  entity: string
  entityId: string
  treeViewLevel: number
  entityData: EntityData
  sharedData: Record<string, any>
}
const TableActionButtons: React.FC<InjectedTableActionComponentProps> = (props) => {
  const basePath = Env.basePath
  const theme: Theme = useTheme()

  const [showErrorText, setShowErrorText] = useState<boolean>(false)
  const [showPasswordDialog, setShowPasswordDialog] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)
  const [showPasswordRepeat, setShowPasswordRepeat] = useState<boolean>(false)
  const [showOldPassword, setShowOldPassword] = useState<boolean>(false)
  const [newPassword, setNewPassword] = useState<string | undefined>()
  const [oldPassword, setOldPassword] = useState<string | undefined>()
  const [confirmPassword, setConfirmPassword] = useState<string | undefined>()

  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  addCsrfHeaders(requestHeaders)

  const openMembershipPdfInNewTab = async (invoiceId: string) => {
    try {
      const url = `${Env.basePath || ''}/api/admin/invoices/annual/membership/pdf?invoiceId=${encodeURIComponent(invoiceId)}`
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/pdf')
      addCsrfHeaders(headers)

      const res = await fetch(url, { method: 'POST', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`PDF Download fehlgeschlagen: HTTP ${res.status} ${text}`)
      }
      const blob = await res.blob()
      const filename = `Rechnung_${props.entityData.membershipNumber}.pdf`
      createAndDownloadFileFromBlob(blob, filename)
    } catch (e) {
      console.error(e)
      alert('PDF konnte nicht geladen werden.')
    }
  }

  const openParticipationdfInNewTab = async (invoiceId: string) => {
    try {
      const url = `${Env.basePath || ''}/api/admin/events/${props.entityData.event}/invoices/${encodeURIComponent(invoiceId)}/pdf`
      const headers: HeadersInit = new Headers()
      headers.set('Accept', 'application/pdf')
      addCsrfHeaders(headers)

      const res = await fetch(url, { method: 'GET', credentials: 'include', headers })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        throw new Error(`PDF Download fehlgeschlagen: HTTP ${res.status} ${text}`)
      }
      const blob = await res.blob()
      const filename = `Rechnung_${props.entityData.membershipNumber}.pdf`
      createAndDownloadFileFromBlob(blob, filename)
    } catch (e) {
      console.error(e)
      alert('PDF konnte nicht geladen werden.')
    }
  }

  const generateEventInvoice = async () => {
    try {
      if (!props.entityId) return
      const url = `${basePath}/api/admin/events/${props.entityData.event}/participants/${props.entityId}/invoice`
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        console.error('Fehler beim Generieren der Veranstaltungsrechnung', res.status, text)
        return
      }
      // Response enthält die Rechnungsdetails
      const json = await res.json().catch(() => null)
      if (!json || !json.invoiceNumber) {
        console.error('Unerwartete Response, keine invoiceNumber vorhanden:', json)
        return
      }
      const invoiceId: string = json.invoiceId
      openParticipationdfInNewTab(invoiceId)
    } catch (e) {
      console.error(e)
      alert('Es ist ein Fehler beim Erzeugen oder Laden der Rechnung aufgetreten.')
    }
  }
  const generateInvoice = async () => {
    try {
      if (!props.entityId) return
      const url = `${basePath}/api/admin/invoices/annual/membership?membershipId=${props.entityId}`
      const res = await fetch(url, {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })
      if (!res.ok) {
        const text = await res.text().catch(() => '')
        console.error('Fehler beim Generieren der Mitgliedsrechnung', res.status, text)
        return
      }
      // Response enthält die Rechnungsdetails
      const json = await res.json().catch(() => null)
      if (!json || !json.invoiceNumber) {
        console.error('Unerwartete Response, keine invoiceNumber vorhanden:', json)
        return
      }
      const invoiceId: string = json.invoiceId
      openMembershipPdfInNewTab(invoiceId)
    } catch (e) {
      console.error(e)
      alert('Es ist ein Fehler beim Erzeugen oder Laden der Rechnung aufgetreten.')
    }
  }

  const createAndDownloadFileFromBlob = (data: Blob, filename: string): void => {
    const dataUrl = window.URL.createObjectURL(data)
    const tempLink = document.createElement('a')
    tempLink.href = dataUrl
    tempLink.setAttribute('download', filename)
    tempLink.click()
    setTimeout(() => URL.revokeObjectURL(dataUrl), 60_000)
  }

  const duplicateEvent = () => {
    const url = `${basePath}/api/events/${props.entityId}/duplicate`
    props.entityId && fetch(url,
      {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => {
        if (res.status < 400) {
          console.log(res)
        }
      })
  }

  const setPassword = () => {
    const url = `${basePath}/api/user/${props.entityId}/password`
    const json = {
      userId: props.entityId,
      newPassword: newPassword,
      oldPassword: oldPassword,
    }
    props.entityId && fetch(url,
      {
        method: 'PUT',
        credentials: 'include',
        headers: requestHeaders,
        body: JSON.stringify(json),
      })
      .then(res => {
        if (res.status < 400) {
          setShowErrorText(false)
          setNewPassword(undefined)
          setOldPassword(undefined)
          setConfirmPassword(undefined)

          setShowPasswordDialog(false)
        } else {
          setShowErrorText(true)
        }
      })
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show)

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleClickShowPasswordRepeat = () => setShowPasswordRepeat((show) => !show)

  const handleMouseDownPasswordRepeat = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  const handleClickShowOldPassword = () => setShowOldPassword((show) => !show)

  const handleMouseDownOldPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault()
  }

  return (
    <>
      {props.entity === 'Membership' &&
        <>
          <Button onClick={() => generateInvoice()}>Rechnung generieren</Button>
        </>}
      {props.entity === 'Participation' &&
        <>
          <Button onClick={() => generateEventInvoice()}>Rechnung generieren</Button>
        </>}
      {props.entity === 'Event' &&
        <>
          <Button onClick={() => duplicateEvent()}>Event duplizieren</Button>
        </>}
      {props.entity === 'User' &&
        <>
          <Button onClick={() => setShowPasswordDialog(true)}>Passwort ändern</Button>
        </>}
      <DialogComponent
        open={showPasswordDialog}
        dialogWidth='400px'
        content={
          <>
            <TextField
              sx={{ margin: '20px 0' }}
              fullWidth
              type={showOldPassword ? 'text' : 'password'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setOldPassword(event.target.value)
              }}
              required
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowOldPassword}
                    onMouseDown={handleMouseDownOldPassword}
                    edge='end'
                  >
                    {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>),
              }}
              label='Altes Passwort'
            />
            <TextField
              fullWidth
              id='outlined-adornment-password'
              type={showPassword ? 'text' : 'password'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setNewPassword(event.target.value)
              }}
              required
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPassword}
                    onMouseDown={handleMouseDownPassword}
                    edge='end'
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>),
              }}
              label='Neues Passwort'
            />
            <TextField
              sx={{ margin: '20px 0' }}
              fullWidth
              id='outlined-adornment-password-repeat'
              type={showPasswordRepeat ? 'text' : 'password'}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setConfirmPassword(event.target.value)
              }}
              required
              InputProps={{
                endAdornment: (
                  <IconButton
                    aria-label='toggle password visibility'
                    onClick={handleClickShowPasswordRepeat}
                    onMouseDown={handleMouseDownPasswordRepeat}
                    edge='end'
                  >
                    {showPasswordRepeat ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>),
              }}
              label='Neues Passwort wiederholen'
            />
            {showErrorText &&
              <Typography variant='body1' sx={{ color: theme.palette.error.main }}>Es ist ein Fehler aufgetreten! Überprüfen Sie Ihre Eingaben!</Typography>}
            <Box sx={{ marginTop: '10px' }}>
              <Button
                color='secondary'
                disabled={!newPassword}
                onClick={() => {
                  setShowPasswordDialog(false)
                }}
              >Abbrechen
              </Button>
              <Button
                color='primary'
                disabled={!newPassword || newPassword !== confirmPassword}
                onClick={() => {
                  if (newPassword) {
                    setPassword()
                  }
                }}
              >Speichern
              </Button>
            </Box>
          </>
        }
        title={<>Passwort ändern</>}
        request={false}
        onClose={() => setShowPasswordDialog(false)}
        autoClose={false}
      />
    </>
  )
}
export default TableActionButtons
